import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useSession } from '../../context/SessionContext';
import { useProject } from '../../context/ProjectContext';
import api from '../../services/api';
import auditService from '../../services/auditService';
import { exportAuditReportToExcel } from '../../utils/exportToExcel';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import './ReportsHome.css';
import './AuditReport.css';

/** Page-wise audit: show only fields that appear on the selected page. Values match API pageKey. CRF Data has its own tile at /reports/crf-data. */
const REPORT_PAGE_OPTIONS = [
  { value: '', label: 'Please select Form Name' },
  { value: 'SiteFeasibilitySelection', label: 'Site Feasibility & Selection' },
  { value: 'SiteOnboardingModule', label: 'Site Onboarding' },
  { value: 'VisitSchedulingPlanner', label: 'Visit Scheduling Planner' },
  { value: 'MonitoringVisitReport', label: 'Monitoring Visit & Report Management' },
  { value: 'MailManagement', label: 'Mail Management' },
];

/** Visit type options for multi-select: value is stored in DB (VisitType). */
const VISIT_TYPE_OPTIONS = [
  { value: 'SSV', label: 'Site Selection Visit' },
  { value: 'SIV', label: 'Site Initiation Visit' },
  { value: 'SMV', label: 'Site Monitoring Visit' },
  { value: 'SCV', label: 'Site Close Out Visit' },
];

function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

function MultiSelectDropdown({ placeholder, options, selectedValues, onChange, disabled, renderLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selectAllRef = useRef(null);
  useOnClickOutside(ref, () => setOpen(false));
  const norm = (v) => (v == null ? '' : String(v));
  const selectedSet = new Set((selectedValues || []).map(norm));
  const allValues = options.map((o) => norm(o.value));
  const allSelected = allValues.length > 0 && allValues.every((v) => selectedSet.has(v));
  const someSelected = selectedSet.size > 0;

  useEffect(() => {
    const el = selectAllRef.current;
    if (el) el.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected, open]);

  const label = renderLabel
    ? options.filter((o) => selectedSet.has(norm(o.value))).map((o) => renderLabel(o)).join(', ')
    : options.filter((o) => selectedSet.has(norm(o.value))).map((o) => o.label).join(', ');
  const summary = selectedValues.length === 0 ? placeholder : (label || `${selectedValues.length} selected`);

  const handleSelectAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange([...allValues]);
    }
  };

  return (
    <div className="audit-multiselect" ref={ref}>
      <button
        type="button"
        className="form-select audit-report-select audit-multiselect-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        title={summary}
      >
        <span className="audit-multiselect-summary">{summary}</span>
        <span className="audit-multiselect-arrow">▼</span>
      </button>
      {open && (
        <div className="audit-multiselect-dropdown">
          <label className="audit-multiselect-option audit-multiselect-select-all">
            <input
              type="checkbox"
              ref={selectAllRef}
              checked={allSelected}
              onChange={handleSelectAll}
            />
            <span>Select All</span>
          </label>
          {options.map((opt) => {
            const optionLabel = renderLabel ? renderLabel(opt) : opt.label;
            return (
              <label key={opt.value} className="audit-multiselect-option" title={optionLabel ?? ''}>
                <input
                  type="checkbox"
                  checked={selectedSet.has(norm(opt.value))}
                  onChange={(e) => {
                    const newSet = new Set((selectedValues || []).map(norm));
                    if (e.target.checked) newSet.add(norm(opt.value));
                    else newSet.delete(norm(opt.value));
                    onChange(Array.from(newSet));
                  }}
                />
                <span>{optionLabel}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

const AuditReport = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { vRecordedSign, vRecordedAtOffset } = useSession();
  const { currentProject: contextProject, projectInfo } = useProject();
  const sessionProject = contextProject && contextProject !== 'all' ? contextProject : null;
  const projectCode = typeof sessionProject === 'string' ? sessionProject : (sessionProject?.projectCode ?? sessionProject?.ProjectCode ?? '');
  const projectLabel = typeof sessionProject === 'object' && sessionProject != null
    ? (sessionProject.projectCode || sessionProject.ProjectCode || sessionProject.projectDescription || '')
    : projectCode;

  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [selectedVisitTypes, setSelectedVisitTypes] = useState([]);
  const [selectedMappingIds, setSelectedMappingIds] = useState([]);
  const [reportPageKey, setReportPageKey] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingSites(true);
    const project = projectCode || undefined;
    api.get(project ? `/sites?project=${encodeURIComponent(project)}` : '/sites')
      .then((res) => {
        if (!cancelled) setSites(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setSites([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSites(false);
      });
    return () => { cancelled = true; };
  }, [projectCode]);

  useEffect(() => {
    let cancelled = false;
    const query = projectCode ? `?projectCode=${encodeURIComponent(projectCode)}` : '';
    api.get(`/users/audit-report-dropdown${query}`)
      .then((res) => {
        if (!cancelled && Array.isArray(res.data)) setUsers(res.data);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      });
    return () => { cancelled = true; };
  }, [projectCode]);

  const handleExportExcel = async () => {
    const siteIdList = selectedSiteIds.filter((id) => id != null && id !== '');
    if (siteIdList.length === 0) {
      setError('Please select at least one Site.');
      return;
    }
    if (!reportPageKey || reportPageKey.trim() === '') {
      setError('Please select Form Name.');
      return;
    }
    setError(null);
    setExportLoading(true);
    try {
      const params = {
        siteIds: siteIdList.map((id) => Number(id)),
        limit: 10000,
      };
      if (projectCode) {
        const pid = projectInfo?.projectId ?? projectInfo?.ProjectId;
        if (pid != null && pid !== '') params.projectId = Number(pid);
        const projNo = projectInfo?.projectNo ?? projectInfo?.ProjectNo
          ?? sessionProject?.projectNo ?? sessionProject?.ProjectNo;
        if (projNo != null) params.projectNo = projNo;
      }
      if (selectedVisitTypes.length > 0) params.visitTypes = selectedVisitTypes;
      // Filter by all selected user+role (e.g. Kamil CRA and Kamil PM): send every selected mapping ID so export includes both profiles
      const mappingIds = Array.isArray(selectedMappingIds)
        ? [...new Set(selectedMappingIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id) && id > 0))]
        : [];
      if (mappingIds.length > 0) params.recordedByMappingIds = mappingIds;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const data = reportPageKey
        ? await auditService.getAuditDetailsReportByPage({ ...params, pageKey: reportPageKey })
        : await auditService.getAuditDetailsReportFiltered(params);
      const list = Array.isArray(data) ? data : [];
      const fileName = `AuditTrailReport_${new Date().toISOString().slice(0, 10)}.xlsx`;
      // Exported by: from session user — First Name + Last Name + (User Name) (Profile)
      const firstName = (user?.firstName ?? user?.FirstName ?? user?.vFirstName ?? '').trim();
      const lastName = (user?.lastName ?? user?.LastName ?? user?.vLastName ?? '').trim();
      const userName = (user?.username ?? user?.Username ?? user?.vLoginName ?? user?.vUserName ?? '').trim();
      const profile = (user?.role ?? user?.Role ?? user?.profileCode ?? user?.vUserTypeCode ?? '').trim();
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      const exportedBy = fullName ? `${fullName} (${userName || '—'})(${profile || '—'})` : (vRecordedSign && vRecordedSign.trim() !== '' ? vRecordedSign.trim() : '');
      const offset = (vRecordedAtOffset && String(vRecordedAtOffset).trim() !== '') ? String(vRecordedAtOffset).trim() : '+5:30';
      const selectedFormOption = REPORT_PAGE_OPTIONS.find((opt) => opt.value === reportPageKey);
      const formNameForExport = selectedFormOption ? selectedFormOption.label : 'All forms';
      exportAuditReportToExcel(list, fileName, {
        formName: formNameForExport,
        exportedBy,
        offset,
        includeCoMonitor: reportPageKey === 'MonitoringVisitReport',
        excludeVisitColumns:
          reportPageKey === 'SiteFeasibilitySelection' || reportPageKey === 'SiteOnboardingModule',
      });
    } catch (err) {
      const apiError = err?.response?.data?.error || err?.message || 'Export failed.';
      const message = typeof apiError === 'string' && apiError.toLowerCase().includes('at least one site')
        ? 'Please select at least one Site.'
        : apiError;
      setError(message);
    } finally {
      setExportLoading(false);
    }
  };

  const siteOptions = sites.map((s) => ({
    value: String(s.siteID ?? s.siteId ?? s.SiteID),
    label: `${s.siteCode ?? s.vSiteCode ?? ''} - ${s.sitedescription ?? s.siteDescription ?? ''}`.trim() || String(s.siteID ?? s.siteId),
  }));
  const userOptions = users.map((u) => ({
    value: String(u.MappingId ?? u.mappingId ?? u.UserId ?? u.userId),
    label: u.DisplayLabel ?? u.displayLabel ?? '',
  }));

  if (loadingSites && sites.length === 0) return <Spinner />;

  return (
    <div className="audit-report">
      <div className="audit-report-header" style={{ marginBottom: '16px' }}>
        <h2>Audit Report</h2>
      </div>

      {error && <Alert type="error" message={error} />}

      <div className="audit-report-filters">
        <div className="audit-report-filter-row">
          <label className="audit-report-label">Project</label>
          <input
            type="text"
            className="form-input audit-report-input"
            value={projectLabel || projectCode || '-'}
            disabled
            readOnly
          />
        </div>
        <div className="audit-report-filter-row">
          <label className="audit-report-label">Site <span className="required">*</span></label>
          <MultiSelectDropdown
            placeholder="Select Site(s)..."
            options={siteOptions}
            selectedValues={selectedSiteIds}
            onChange={setSelectedSiteIds}
            renderLabel={(o) => o.label}
          />
        </div>
        <div className="audit-report-filter-row">
          <label className="audit-report-label">Form Name <span className="required">*</span></label>
          <select
            className="form-select audit-report-select"
            value={reportPageKey}
            onChange={(e) => setReportPageKey(e.target.value)}
            title="Select a page to show only audit fields that appear on that page"
          >
            {REPORT_PAGE_OPTIONS.map((opt, i) => (
              <option key={i} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {reportPageKey === 'MonitoringVisitReport' && (
          <div className="audit-report-filter-row">
            <label className="audit-report-label">Visit</label>
            <MultiSelectDropdown
              placeholder="All visit types"
              options={VISIT_TYPE_OPTIONS}
              selectedValues={selectedVisitTypes}
              onChange={setSelectedVisitTypes}
              renderLabel={(o) => o.label}
            />
          </div>
        )}
        <div className="audit-report-filter-row">
          <label className="audit-report-label">User</label>
          <MultiSelectDropdown
            placeholder="All users"
            options={userOptions}
            selectedValues={selectedMappingIds}
            onChange={setSelectedMappingIds}
            renderLabel={(o) => o.label}
          />
        </div>
        <div className="audit-report-filter-row">
          <label className="audit-report-label">From Date</label>
          <input
            type="date"
            className="form-input audit-report-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="audit-report-filter-row">
          <label className="audit-report-label">To Date</label>
          <input
            type="date"
            className="form-input audit-report-input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>

      <div className="audit-report-actions">
        <button
          type="button"
          className="btn btn-primary export-excel-btn"
          onClick={handleExportExcel}
          disabled={exportLoading || selectedSiteIds.length === 0 || !reportPageKey}
          title={
            !reportPageKey ? 'Please select Form Name' :
            selectedSiteIds.length === 0 ? 'Select at least one site to export' :
            exportLoading ? 'Exporting…' : 'Export to Excel'
          }
        >
          {exportLoading ? 'Exporting…' : 'Export'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/reports')}>
          Close
        </button>
      </div>

      
    </div>
  );
};

export default AuditReport;
